import { useQuery } from "@tanstack/react-query"
import { AlertCircle, AlertTriangle, ChartColumn } from "lucide-react"

import { api } from "@/api/cliente"
import { NATURALEZAS, type Naturaleza } from "@/componentes/ordenes/naturaleza"
import { aCentimos, comoMonto } from "@/componentes/tesoreria/dinero"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"

type Grupo = {
  clave: string
  etiqueta: string
  total: string
  ordenes: number
  tipo_gasto: string | null
}
type Excluida = { codigo: string; divisa: string; monto: string; motivo: string }
type Informe = {
  anio: number
  mes: number
  moneda: string
  total: string
  ordenes: number
  por_categoria: Grupo[]
  por_naturaleza: Grupo[]
  por_tipo: Grupo[]
  comprometido: string
  ordenes_comprometidas: number
  sin_convertir: Excluida[]
}

const ETIQUETA_TIPO: Record<string, string> = {
  FIJO: "Fijos",
  VARIABLE: "Variables",
  SIN_CLASIFICAR: "Sin clasificar",
}

/** El porcentaje que representa una parte, en entero. Cero cuando no hay total. */
function porcentaje(parte: bigint, total: bigint): number {
  if (total <= 0n) return 0
  return Number((parte * 1000n) / total) / 10
}

/**
 * La composición del gasto del período: fijo contra variable y por lo que se compró.
 *
 * ⚠ **Recortado el 28/10** («estamos redundando»): el total ejecutado, lo comprometido y la
 * tabla por categoría ya los dice el reporte de cuentas y resultados, justo encima en
 * Administración → Reportes, con las mismas cifras. Aquí queda lo que él no dice.
 */
export function ContenidoInforme({
  anio,
  mes,
  periodo,
}: {
  anio?: number
  mes?: number
  /** `periodo=SEMANA&fecha=…` (28/10): semana, 15, 30, 60 días o mes. Manda sobre año y mes. */
  periodo?: string
}) {
  const filtro = periodo ?? `anio=${anio}&mes=${mes}`
  const consulta = useQuery({
    queryKey: ["informe-gastos", filtro],
    queryFn: () => api.get<Informe>(`/api/v1/informes/gastos?${filtro}`),
  })
  const datos = consulta.data
  const total = datos ? aCentimos(datos.total) : 0n

  return (
    <div className="space-y-4">
      {consulta.isPending && <p className="py-6">Cargando…</p>}
      {consulta.isError && (
        <p className="flex items-center gap-2 py-6" style={{ color: "var(--destructive)" }}>
          <AlertCircle size={16} /> No se pudo cargar el informe de este período.
        </p>
      )}

      {datos && (
        <>
          {/* ⚠ Lo que no se pudo convertir NO desaparece: sin esto, el total saldría
              corto y nadie sabría por qué. Es el mismo trato que los pendientes del
              libro de compras. */}
          {datos.sin_convertir.length > 0 && (
            <div role="alert" className="tarjeta p-3 text-sm" style={{ color: "var(--warning)" }}>
              <p className="flex items-center gap-1.5 font-medium">
                <AlertTriangle size={14} /> {datos.sin_convertir.length}{" "}
                {datos.sin_convertir.length === 1 ? "orden queda" : "órdenes quedan"} fuera del
                total
              </p>
              <ul className="mt-1 space-y-0.5 pl-6 text-xs">
                {datos.sin_convertir.map((e) => (
                  <li key={e.codigo}>
                    {e.codigo} · {e.monto} {e.divisa} — {e.motivo}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h2 className="mb-2 text-sm font-semibold">Fijo contra variable</h2>
              <div className="tarjeta p-4">
                <p className="text-sm font-semibold tabular-nums">
                  {comoMonto(total)} {datos.moneda} ejecutados
                </p>
                <div className="mt-2 space-y-1.5">
                  {datos.por_tipo.length === 0 && (
                    <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
                      —
                    </p>
                  )}
                  {datos.por_tipo.map((g) => (
                    <div key={g.clave}>
                      <p className="flex justify-between text-xs">
                        <span>{ETIQUETA_TIPO[g.clave] ?? g.etiqueta}</span>
                        <span className="tabular-nums">
                          {comoMonto(aCentimos(g.total))} ·{" "}
                          {porcentaje(aCentimos(g.total), total).toFixed(0)} %
                        </span>
                      </p>
                      <Barra parte={aCentimos(g.total)} total={total} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h2 className="mb-2 text-sm font-semibold">Por lo que se compró</h2>
              <Tabla etiqueta="Gasto del mes por naturaleza del renglón">
                <Cabecera>
                  <Th>Qué es</Th>
                  <Th derecha>Total</Th>
                  <Th derecha>%</Th>
                </Cabecera>
                <Cuerpo>
                  {datos.por_naturaleza.length === 0 && (
                    <TablaVacia colSpan={3}>No hay gasto ejecutado en este período.</TablaVacia>
                  )}
                  {datos.por_naturaleza.map((g) => {
                    const n = NATURALEZAS[g.clave as Naturaleza]
                    return (
                      <Fila key={g.clave}>
                        <Td>
                          {n ? (
                            <Insignia
                              tono={n.tono}
                              icono={<n.Icono size={12} strokeWidth={2} aria-hidden="true" />}
                            >
                              {n.etiqueta}
                            </Insignia>
                          ) : (
                            g.etiqueta
                          )}
                          <Barra parte={aCentimos(g.total)} total={total} />
                        </Td>
                        <Td derecha numero>
                          {comoMonto(aCentimos(g.total))}
                        </Td>
                        <Td
                          derecha
                          numero
                          data-exportar-valor={porcentaje(aCentimos(g.total), total)
                            .toFixed(1)
                            .replace(".", ",")}
                        >
                          {porcentaje(aCentimos(g.total), total).toFixed(1)}
                        </Td>
                      </Fila>
                    )
                  })}
                </Cuerpo>
              </Tabla>
              <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
                <ChartColumn size={12} className="mr-1 inline" aria-hidden="true" />
                Una factura puede traer varias cosas a la vez: cada orden reparte su importe entre
                las naturalezas de sus renglones, así que la tabla suma lo ejecutado.
              </p>
            </div>
          </div>

          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            Convertido a {datos.moneda} con la tasa del día de emisión de cada orden, no con la de
            hoy. El mes lo decide la fecha de la factura, no la del pago.
          </p>
        </>
      )}
    </div>
  )
}

/**
 * La proporción, dibujada.
 *
 * ⚠ **Acompaña a la cifra, nunca la sustituye** (WCAG 1.4.1): el ancho es el refuerzo
 * y el número es el dato, que además está en su columna. Por eso es `aria-hidden`: un
 * lector de pantalla ya leyó el importe y el porcentaje.
 */
function Barra({ parte, total }: { parte: bigint; total: bigint }) {
  return (
    <span
      aria-hidden="true"
      className="mt-1 block h-1 w-full overflow-hidden rounded-full"
      style={{ background: "var(--cristal-relleno)" }}
    >
      <span
        className="block h-full rounded-full"
        style={{
          width: `${porcentaje(parte, total)}%`,
          background: "var(--primary)",
        }}
      />
    </span>
  )
}
